import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useFavorites } from '../context/FavoritesContext';
import { useCart } from '../context/CartContext';

const FavoritesPage = () => {
  const { favoriteItems, removeFromFavorites, clearFavorites } = useFavorites();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const { country } = useParams();

  const handleProductClick = (productId) => {
    navigate(`/${country}/products/${productId}`);
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
  };

  return (
    <div className="min-h-screen bg-aug-cream">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-[1400px] mx-auto px-xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Heart className="w-8 h-8 fill-red-500 text-red-500" />
              <div>
                <h1 className="text-4xl font-bold" style={{ color: '#1B5E20' }}>
                  Meus Favoritos
                </h1>
                <p className="text-aug-gray-medium">
                  {favoriteItems.length} {favoriteItems.length === 1 ? 'produto' : 'produtos'} salvos
                </p>
              </div>
            </div>
            
            {favoriteItems.length > 0 && (
              <button
                onClick={clearFavorites}
                className="flex items-center gap-2 px-4 py-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Limpar Tudo</span>
              </button>
            )}
          </div>

          {/* Content */}
          {favoriteItems.length === 0 ? (
            <div className="bg-white rounded-xl p-16 text-center">
              <Heart className="w-24 h-24 mx-auto mb-4 opacity-20" />
              <h2 className="text-2xl font-bold text-aug-gray-dark mb-2">
                Nenhum favorito ainda
              </h2>
              <p className="text-aug-gray-medium mb-6">
                Explore nossos produtos e adicione seus favoritos aqui!
              </p>
              <button
                onClick={() => navigate(`/${country}/products`)}
                className="px-6 py-3 rounded-lg font-semibold text-white transition-colors"
                style={{ backgroundColor: '#1B5E20' }}
              >
                Explorar Produtos
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {favoriteItems.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                >
                  {/* Image */}
                  <div
                    onClick={() => handleProductClick(product.id)}
                    className="relative h-64 overflow-hidden bg-gray-100 cursor-pointer group"
                  >
                    <img
                      src={product.images?.[0] || 'https://via.placeholder.com/400'}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFromFavorites(product.id);
                      }}
                      className="absolute top-2 right-2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-50 transition-colors"
                    >
                      <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                    </button>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <p className="text-xs font-medium mb-1" style={{ color: '#1B5E20' }}>
                      {product.category_name}
                    </p>
                    <h3
                      onClick={() => handleProductClick(product.id)}
                      className="text-base font-semibold text-aug-gray-dark mb-2 line-clamp-2 cursor-pointer hover:text-aug-blue"
                    >
                      {product.name}
                    </h3>

                    {/* Price */}
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-2xl font-bold" style={{ color: '#1B5E20' }}>
                        R$ {(product.sale_price || product.regular_price).toFixed(2)}
                      </span>
                      {product.sale_price && product.sale_price < product.regular_price && (
                        <span className="text-sm text-aug-gray-medium line-through">
                          R$ {product.regular_price.toFixed(2)}
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAddToCart(product)}
                        className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-medium text-white transition-colors"
                        style={{ backgroundColor: '#1B5E20' }}
                        disabled={!product.in_stock}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        <span className="text-sm">
                          {product.in_stock ? 'Adicionar' : 'Esgotado'}
                        </span>
                      </button>
                      <button
                        onClick={() => removeFromFavorites(product.id)}
                        className="px-3 py-2 border border-red-300 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FavoritesPage;
